import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  verifyEmailDomain,
  fetchDomainConfig,
  setAutoPromote,
  authTone,
  authLabel,
  BRAND_DOMAIN,
  PROVIDER_DOMAIN,
  type DomainVerifyResult,
  type DomainConfig,
} from '@/lib/emailAuth';


/**
 * Admin → Email delivery → "Domain authentication (launch blocker)".
 *
 * This is the pre-launch gate. It runs the live DNS verifier, shows the five
 * ordered alignment tasks, the exact records to publish (copyable), which of
 * support@ / noreply@ can currently be used as a From: identity, and the
 * per-provider inbox-placement score now vs. after the fix.
 */

const Pill: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${authTone(status)}`}>
    {authLabel(status)}
  </span>
);

const Score: React.FC<{ label: string; now: number; then: number }> = ({ label, now, then }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`mt-1 text-2xl font-extrabold ${now >= 85 ? 'text-emerald-600' : now >= 65 ? 'text-amber-600' : 'text-red-600'}`}>
      {now}
      <span className="text-sm font-semibold text-slate-400">/100</span>
    </p>
    <p className="mt-0.5 text-[11px] font-medium text-slate-500">→ {then} after fix</p>
  </div>
);

const AdminDnsAlignment: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [res, setRes] = useState<DomainVerifyResult | null>(null);
  const [cfg, setCfg] = useState<DomainConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  const run = async () => {
    setBusy(true);
    setErr('');
    const out = await verifyEmailDomain(true);
    if ('error' in out) setErr(out.error);
    else setRes(out);
    setCfg(await fetchDomainConfig());
    setBusy(false);
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (v: string, id: string) => {
    navigator.clipboard?.writeText(v);
    setCopied(id);
    setTimeout(() => setCopied(''), 1600);
  };

  const toggleAuto = async () => {
    if (!cfg) return;
    await setAutoPromote(cfg.id, !cfg.auto_promote);
    setCfg(await fetchDomainConfig());
  };

  const statusFor = (taskId: string) => res?.tasks?.find((t) => t.id === taskId)?.status;

  return (
    <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-extrabold tracking-tight text-slate-900">
              {es ? 'Autenticación de dominio' : 'Domain authentication'}
            </h2>
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700 ring-1 ring-red-200">
              {es ? 'BLOQUEA EL LANZAMIENTO' : 'LAUNCH BLOCKER'}
            </span>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            {es
              ? 'SPF, DKIM, DMARC, Return-Path e identidades verificados contra el DNS público real (dos resolvers). El problema de entrega es la alineación, no la plantilla.'
              : 'SPF, DKIM, DMARC, Return-Path and sender identities checked against real public DNS (two independent resolvers). The deliverability problem is alignment, not the template.'}
          </p>
        </div>
        <button
          onClick={run}
          disabled={busy}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? (es ? 'Verificando DNS…' : 'Checking DNS…') : es ? 'Verificar de nuevo' : 'Re-verify DNS now'}
        </button>
      </div>

      {err && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700 ring-1 ring-red-200">{err}</p>}

      {res && (
        <>
          {/* Scores */}
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Score label="Gmail" now={res.providerScores.gmail} then={res.projectedScores.gmail} />
            <Score label="Outlook" now={res.providerScores.outlook} then={res.projectedScores.outlook} />
            <Score label="Yahoo" now={res.providerScores.yahoo} then={res.projectedScores.yahoo} />
            <Score label="Apple Mail" now={res.providerScores.apple} then={res.projectedScores.apple} />
          </div>

          {/* Current sending identity */}
          <div
            className={`mt-4 rounded-xl p-4 ring-1 ${
              res.allAligned ? 'bg-emerald-50 ring-emerald-200' : 'bg-amber-50 ring-amber-200'
            }`}
          >
            <p className="text-sm font-bold text-slate-900">
              {es ? 'Enviando ahora como' : 'Currently sending as'}{' '}
              <span className="font-mono">@{res.activeFromDomain}</span>
              {res.activeFromDomain === PROVIDER_DOMAIN && (
                <span className="font-normal"> · Reply-To support@{BRAND_DOMAIN}</span>
              )}
            </p>
            <p className="mt-1 text-sm text-slate-700">{res.inboxExpectation}</p>
            {cfg && (
              <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-700">
                <input type="checkbox" checked={cfg.auto_promote} onChange={toggleAuto} className="h-4 w-4" />
                {es
                  ? `Promover automáticamente el remitente a @${BRAND_DOMAIN} cuando SPF+DKIM+DMARC pasen`
                  : `Auto-promote the From: identity to @${BRAND_DOMAIN} the moment SPF + DKIM + DMARC pass`}
              </label>
            )}
          </div>

          {/* The five tasks */}
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Tareas de alineación en orden' : 'Alignment tasks, in order'}
          </h3>
          <ol className="mt-2 space-y-2">
            {res.tasks.map((t) => (
              <li key={t.id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">
                    {t.order}
                  </span>
                  <span className="text-sm font-bold text-slate-900">{t.title}</span>
                  <Pill status={t.status} />
                  {t.blocking && (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-red-200">
                      {es ? 'BLOQUEANTE' : 'BLOCKING'}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-600">{t.detail}</p>
                <p className="mt-1 text-xs font-medium text-slate-500">{t.fix}</p>
              </li>
            ))}
          </ol>

          {/* Identities */}
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Identidades del remitente' : 'Sender identities'}
          </h3>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">{es ? 'Dirección' : 'Address'}</th>
                  <th className="py-2 pr-3">SPF</th>
                  <th className="py-2 pr-3">DKIM</th>
                  <th className="py-2 pr-3">DMARC</th>
                  <th className="py-2 pr-3">{es ? 'Enviando como' : 'Sending as'}</th>
                </tr>
              </thead>
              <tbody>
                {res.identities.map((i) => (
                  <tr key={i.address} className="border-b border-slate-100 align-top">
                    <td className="py-2 pr-3">
                      <p className="font-bold text-slate-900">{i.displayName}</p>
                      <p className="font-mono text-xs text-slate-500">{i.address}</p>
                    </td>
                    <td className="py-2 pr-3"><Pill status={i.spf} /></td>
                    <td className="py-2 pr-3"><Pill status={i.dkim} /></td>
                    <td className="py-2 pr-3"><Pill status={i.dmarc} /></td>
                    <td className="py-2 pr-3">
                      <p className="font-mono text-xs text-slate-700">{i.sendingAs}</p>
                      <p className="mt-1 max-w-sm text-[11px] text-slate-500">{i.note}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* The exact records live in the GoDaddy guide panel directly above,
              which reads the real key + published state from the provider. */}
          <p className="mt-6 rounded-xl bg-slate-50 p-3 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
            {es
              ? 'Los valores exactos a pegar en GoDaddy (tipo, host, valor, TTL y si se agrega o reemplaza) están en el panel “Guía DNS de GoDaddy” arriba.'
              : 'The exact values to paste into GoDaddy — type, host, value, TTL and whether to add or replace — are in the “GoDaddy DNS guide” panel above.'}
          </p>


          {/* Raw records found */}
          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Lo que hay publicado ahora' : 'What is published right now'}
          </h3>
          <div className="mt-2 space-y-2 text-xs">
            {[
              { k: 'SPF', v: res.spf?.record, s: res.spf?.status, n: res.spf?.note },
              {
                k: 'DKIM',
                v: res.dkim?.selectorsFound?.length
                  ? res.dkim.selectorsFound.map((d: any) => `${d.selector} (${d.owner})`).join(', ')
                  : null,
                s: res.dkim?.status,
                n: res.dkim?.note,
              },
              { k: 'DMARC', v: res.dmarc?.record, s: res.dmarc?.status, n: res.dmarc?.note },
              { k: 'Return-Path', v: res.bounce?.cname, s: res.bounce?.status, n: res.bounce?.note },
              { k: 'MX', v: res.mx?.records?.join(', '), s: res.mx?.status, n: res.mx?.note },
            ].map((row) => (
              <div key={row.k} className="rounded-lg bg-slate-50 p-3">
                <div className="flex items-center gap-2">
                  <span className="w-24 shrink-0 font-bold text-slate-700">{row.k}</span>
                  <Pill status={row.s} />
                  <code className="break-all font-mono text-[11px] text-slate-600">{row.v || (es ? 'no publicado' : 'not published')}</code>
                </div>
                <p className="mt-1 pl-0 text-[11px] text-slate-500 sm:pl-[104px]">{row.n}</p>
              </div>
            ))}
          </div>

          <p className="mt-4 text-[11px] text-slate-400">
            {es ? 'Última verificación' : 'Last verified'}: {new Date(res.checkedAt).toLocaleString()}
            {res.readOnly && (es ? ' · solo lectura' : ' · read-only run')}
            {res.promoted && (es ? ' · remitente promovido' : ' · From: identity promoted')}
          </p>
        </>
      )}
    </section>
  );
};

export default AdminDnsAlignment;
